/**
 * QueryState — componentes reutilizáveis para estados de loading/erro de queries.
 *
 * Centraliza o padrão: if (isLoading) return <LoadingSpinner>
 *                      if (error) return <QueryError>
 *
 * Acessibilidade:
 *   - LoadingSpinner: role="status" + aria-label descritivo
 *   - QueryError: role="alert" + aria-live="assertive"
 *   - prefers-reduced-motion: spinner para de girar mas permanece visível
 */
import { AlertCircle, RefreshCw } from 'lucide-react';
import { C } from '@/theme/palette';
import { ForbiddenBanner } from './ForbiddenBanner';
import { RateLimitBanner } from './RateLimitBanner';

/**
 * LoadingSpinner — spinner inline Nava.
 *
 * @param {{ label?: string, size?: number }} props
 */
export function LoadingSpinner({ label = 'Carregando...', size = 28 }) {
  return (
    <div
      role="status"
      aria-label={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 48,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          border: `2px solid ${C.blackBorder}`,
          borderTopColor: C.cyber,
          // A animação é declarada no NavaStyles (navaSpin)
          animation: 'navaSpin 0.8s linear infinite',
        }}
      />
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          [aria-label="${label}"] div { animation: none; border-top-color: ${C.cyber}; }
        }
      `}</style>
    </div>
  );
}

/**
 * QueryError — exibe erro de query de forma amigável.
 * Para erros 403 usa o ForbiddenBanner especializado.
 *
 * @param {{ error: import('@/api/client').ApiError, onRetry?: () => void }} props
 */
export function QueryError({ error, onRetry }) {
  if (error?.isForbidden) {
    return <ForbiddenBanner detail={error.detail} />;
  }

  if (error?.status === 429) {
    return <RateLimitBanner detail={error.detail} />;
  }

  const msg = error?.detail || error?.title || error?.message || 'Erro ao carregar dados. Tente novamente.';

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '16px 20px',
        borderRadius: 12,
        backgroundColor: `${C.hot}14`,
        border: `1px solid ${C.hot}44`,
        marginBottom: 24,
      }}
    >
      <AlertCircle
        size={18}
        aria-hidden="true"
        style={{ color: C.hot, flexShrink: 0, marginTop: 1 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.hot, marginBottom: 4 }}>
          Erro ao carregar
        </div>
        <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.5 }}>{msg}</div>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          aria-label="Tentar novamente"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: C.neutral,
            background: C.blackElev,
            border: `1px solid ${C.blackBorder}`,
            borderRadius: 8,
            minHeight: 36,
            padding: '0 12px',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <RefreshCw size={13} aria-hidden="true" />
          Tentar novamente
        </button>
      )}
    </div>
  );
}

/**
 * MutationError — banner de erro de mutation (PUT/POST).
 * Diferente do QueryError: fica acima do formulário e tem ação de dismiss.
 *
 * @param {{ error: import('@/api/client').ApiError, onDismiss?: () => void }} props
 */
export function MutationError({ error, onDismiss }) {
  if (!error) return null;
  if (error.isForbidden) return <ForbiddenBanner detail={error.detail} />;
  if (error.status === 429) return <RateLimitBanner detail={error.detail} />;

  const msg = error.detail || error.title || error.message || 'Erro ao salvar. Tente novamente.';

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 16px',
        borderRadius: 12,
        backgroundColor: `${C.hot}11`,
        border: `1px solid ${C.hot}44`,
        marginBottom: 16,
        marginTop: 12,
      }}
    >
      <AlertCircle
        size={16}
        aria-hidden="true"
        style={{ color: C.hot, flexShrink: 0, marginTop: 1 }}
      />
      <div style={{ flex: 1, fontSize: 13, color: C.hot }}>{msg}</div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Fechar mensagem de erro"
          style={{
            color: C.neutralMuted,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
